import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  Globe, Eye, Users, BarChart3, ExternalLink, Plus,
  Loader2, Layout, LogOut, ChevronRight, Clock, CheckCircle2, AlertTriangle
} from "lucide-react";
import { Logo } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOMAIN_STATUS = {
  active: { label: "Live", color: "bg-green-100 text-green-700", icon: CheckCircle2 },
  pending_approval: { label: "Wartet auf Freigabe", color: "bg-yellow-100 text-yellow-700", icon: Clock },
  registered: { label: "Registriert", color: "bg-green-100 text-green-700", icon: CheckCircle2 },
  pending: { label: "Ausstehend", color: "bg-gray-100 text-gray-600", icon: Clock },
  failed: { label: "Fehlgeschlagen", color: "bg-red-100 text-red-700", icon: AlertTriangle },
  rejected: { label: "Abgelehnt", color: "bg-red-100 text-red-700", icon: AlertTriangle },
};

function PageCard({ page }) {
  const navigate = useNavigate();
  const domain = page.custom_domain || (page.subdomain ? `${page.subdomain}.webgenerator.io` : null);
  const statusCfg = DOMAIN_STATUS[page.domain_status] || DOMAIN_STATUS.pending;
  const StatusIcon = statusCfg.icon;
  const isLive = page.deployment_status === "active" && page.is_published;

  return (
    <div data-testid={`page-card-${page.id}`}
      className="bg-white rounded-xl border border-slate-200 hover:border-indigo-200 hover:shadow-md transition-all p-5 cursor-pointer"
      onClick={() => navigate(`/dashboard/${page.id}`)}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-semibold text-slate-900 text-lg">{page.company_name}</h3>
          <p className="text-sm text-slate-500">{page.industry} &middot; {page.city}</p>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusCfg.color}`}>
          <StatusIcon className="w-3 h-3" />{statusCfg.label}
        </span>
      </div>

      {domain && (
        <div className="flex items-center gap-2 text-sm text-slate-600 mb-3">
          <Globe className="w-3.5 h-3.5 text-slate-400" />
          <span className="font-mono text-xs truncate">{domain}</span>
          {isLive && (
            <a href={`${window.location.origin}/page/${page.id}`} target="_blank" rel="noreferrer"
              onClick={e => e.stopPropagation()} className="ml-auto text-indigo-600 hover:text-indigo-800">
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      )}

      <div className="flex items-center gap-4 text-xs text-slate-500">
        <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{page.visitor_count || 0} Besucher</span>
        <span className="flex items-center gap-1"><Users className="w-3 h-3" />{page.lead_count || 0} Anfragen</span>
        {page.subscription?.package_name && (
          <span className="flex items-center gap-1 ml-auto text-indigo-600 font-medium">{page.subscription.package_name}</span>
        )}
      </div>

      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
        <span className="text-xs text-slate-400">
          Erstellt: {new Date(page.created_at).toLocaleDateString("de-DE")}
        </span>
        <ChevronRight className="w-4 h-4 text-slate-400" />
      </div>
    </div>
  );
}

export default function MyPagesPage() {
  const { user, token, logout, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate("/anmelden?redirect=/meine-seiten");
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (token) fetchPages();
  }, [token]);

  const fetchPages = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/user/pages`, { headers: { Authorization: `Bearer ${token}` } });
      setPages(res.data.pages || []);
    } catch (e) {
      if (e.response?.status === 401) { logout(); navigate("/anmelden?redirect=/meine-seiten"); }
      else toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 text-indigo-600 animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/")} className="flex items-center">
              <Logo size="md" clickable={false} />
            </button>
            <span className="text-slate-300">|</span>
            <span className="text-sm font-medium text-slate-600">Meine Seiten</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500 hidden sm:block">{user?.email}</span>
            <Button data-testid="logout-btn" size="sm" variant="ghost" onClick={() => { logout(); navigate("/"); }}>
              <LogOut className="w-4 h-4 mr-1" />Abmelden
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Meine Seiten</h1>
            <p className="text-slate-500 text-sm mt-1">{pages.length} {pages.length === 1 ? "Seite" : "Seiten"} erstellt</p>
          </div>
          <Button data-testid="create-new-page" onClick={() => navigate("/assistent")} className="bg-indigo-600 hover:bg-indigo-700">
            <Plus className="w-4 h-4 mr-1.5" />Neue Seite erstellen
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-20"><Loader2 className="w-8 h-8 text-indigo-600 animate-spin mx-auto" /></div>
        ) : pages.length === 0 ? (
          <div data-testid="empty-pages" className="text-center py-20">
            <Layout className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-700 mb-2">Noch keine Seiten</h2>
            <p className="text-slate-500 mb-6">Erstelle deine erste professionelle Landingpage in unter 2 Minuten.</p>
            <Button onClick={() => navigate("/assistent")} className="bg-indigo-600 hover:bg-indigo-700">
              <Plus className="w-4 h-4 mr-1.5" />Jetzt starten
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {pages.map(p => <PageCard key={p.id} page={p} />)}
          </div>
        )}
      </main>
    </div>
  );
}
